import { Component } from '@theme/component';

/**
 * Announcement bar: a right-to-left ticker that shows one message at a time.
 * Each message travels the full width of the ticker viewport — starting
 * fully off-screen to the right and finishing fully off-screen to the left
 * — using its own real rendered width and the viewport's real rendered
 * width (via the Web Animations API), so the distance is always correct
 * regardless of message length or screen size. The next message only
 * starts once the current one's `finished` promise resolves, so messages
 * never overlap or cut off mid-sentence.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} ticker
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  static PIXELS_PER_SECOND = 130;
  static REDUCED_MOTION_READ_MS = 3500;

  #current = 0;

  /** @type {Animation | undefined} */
  #animation;

  #paused = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #reducedMotionTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // ResizeObserver also fires once on observe(), but that first callback
    // isn't guaranteed to run promptly (e.g. while the tab is backgrounded),
    // so kick off the ticker directly here rather than relying on it solely.
    this.#restart();

    this.#resizeObserver = new ResizeObserver(() => this.#restart());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#animation?.cancel();
    clearTimeout(this.#reducedMotionTimeout);
    this.#resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    this.#current = current;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    this.#restart();
  }

  /**
   * Whether there's more than one message to rotate through.
   */
  get autoplay() {
    return (this.refs.slides ?? []).length > 1;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #activeText() {
    const slides = this.refs.slides ?? [];
    const active = slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
    return active?.querySelector('[data-marquee-text]') ?? null;
  }

  /**
   * Cancels whatever is currently running and starts (or restarts) the
   * ticker for the currently active message, using freshly measured
   * dimensions. Called on init, on navigation (arrows), and on resize.
   */
  #restart() {
    this.#animation?.cancel();
    clearTimeout(this.#reducedMotionTimeout);

    if (!this.isConnected) return;

    const text = this.#activeText();
    const viewport = this.refs.ticker;
    if (!text || !viewport) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      text.style.transform = 'translateX(0)';

      if (!this.autoplay) return;

      this.#reducedMotionTimeout = window.setTimeout(() => this.next(), AnnouncementBar.REDUCED_MOTION_READ_MS);
      return;
    }

    const viewportWidth = viewport.getBoundingClientRect().width;
    const textWidth = text.getBoundingClientRect().width;
    const distance = viewportWidth + textWidth;
    const duration = (distance / AnnouncementBar.PIXELS_PER_SECOND) * 1000;

    this.#animation = text.animate(
      [{ transform: `translateX(${viewportWidth}px)` }, { transform: `translateX(-${textWidth}px)` }],
      { duration, easing: 'linear', fill: 'forwards' }
    );

    if (this.#paused) this.#animation.pause();

    this.#animation.finished
      .then(() => {
        if (!this.autoplay) return;
        this.next();
      })
      .catch(() => {
        // The animation was cancelled (resize, manual navigation, or the
        // component was disconnected) — a new run is already taking over,
        // or there's nothing left to do.
      });
  }

  #pause = () => {
    this.#paused = true;
    this.#animation?.pause();
  };

  #resume = () => {
    this.#paused = false;
    if (this.#animation?.playState === 'paused') this.#animation.play();
  };

  #handleVisibilityChange = () => (document.hidden ? this.#pause() : this.#resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
